import { useState } from 'react';
import { ChevronDown, HelpCircle, ArrowRight } from 'lucide-react';
import { tracking } from '../../utils/tracking';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-6 text-left group"
      >
        <span className="font-medium text-slate-900 pr-8 group-hover:text-coral-600 transition-colors text-lg">
          {question}
        </span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
          isOpen ? 'bg-coral-100 text-coral-600' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'
        }`}>
          <ChevronDown
            className={`h-5 w-5 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

interface FAQProps {
  onStartFree?: () => void;
}

export function FAQ({ onStartFree }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'Is this therapy?',
      answer: 'No, it\'s coaching. Think of it like having a smart friend who gives you real advice and helps you stay on track. If you\'re dealing with something serious, please talk to a real therapist - this isn\'t a replacement for that.',
    },
    {
      question: 'What can I ask the coach?',
      answer: 'Pretty much anything. Career stuff, relationships, habits, feeling stuck, motivation, overthinking - whatever\'s on your mind. The coach adapts to what you need.',
    },
    {
      question: 'How is this different from ChatGPT?',
      answer: 'Three things: Coach remembers your context across conversations, it\'s designed specifically for personal growth (not coding or essays), and it creates actual action plans you can track. Plus it doesn\'t forget who you are every session.',
    },
    {
      question: 'Do I have to journal every day?',
      answer: 'Nope. Use it when you need it. Some people journal daily, some weekly, some just when something\'s on their mind. There\'s no wrong way.',
    },
    {
      question: 'Do I need to already be disciplined?',
      answer: 'No. The whole point is to help you build consistency over time, not assume you already have it. Think of it as structure when motivation runs out.',
    },
    {
      question: 'Is my stuff private?',
      answer: 'Yes. No public profiles, no social features, no one sees your journal or chats. Just you. We don\'t sell your data or use it to train models.',
    },
    {
      question: 'Is it really free?',
      answer: 'Yes. The free plan is free forever - no credit card, no trial that expires. You get 3 coaching sessions per week and 10 journal entries per month. If you want unlimited access, premium is $9/month and you can cancel anytime.',
    },
    {
      question: 'What if I have questions?',
      answer: 'Email us at team@vzmo.ai. Real humans reply, usually within a day.',
    },
  ];

  const handleToggle = (index: number) => {
    const newIndex = openIndex === index ? null : index;
    setOpenIndex(newIndex);
    if (newIndex !== null) {
      tracking.faqOpened(faqs[index].question);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full text-slate-700 text-sm font-medium mb-6">
            <HelpCircle className="h-4 w-4" />
            <span>Got questions?</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
            Frequently asked
          </h2>
        </div>

        <div className="bg-[#FDFBF7] rounded-3xl p-6 sm:p-8">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>

        {onStartFree && (
          <div className="text-center mt-12">
            <p className="text-slate-600 mb-6">Ready to get unstuck?</p>
            <button
              onClick={onStartFree}
              className="group inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold text-lg rounded-full hover:bg-slate-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Get started free
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <p className="text-sm text-slate-500 mt-4">Free forever. No credit card required.</p>
          </div>
        )}
      </div>
    </section>
  );
}
